import getLink, { LINK_TYPE } from "../../../config/Links";

import takyonLight from "../../../assets/img/icons/TakyonLight.svg";
import facebook from "../../../assets/img/icons/facebook.svg";
import instagram from "../../../assets/img/icons/instagram.svg";
import linkedin from "../../../assets/img/icons/linkedin.svg";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { fireTagManagerEvent } from "../../../assets/Services/tagmanager";

const pagesWithFooter = [
  getLink(LINK_TYPE.SECONDARY_MARKET),
  getLink(LINK_TYPE.TAK),
  getLink(LINK_TYPE.PRIVACY),
  getLink(LINK_TYPE.COOCKIES),
  getLink(LINK_TYPE.SUPPORT),
  getLink(LINK_TYPE.INNOVATIVE_STARTUP),
  getLink(LINK_TYPE.TOS),
  getLink(LINK_TYPE.DIRECT_SEARCH),
];

export interface footerItemsProps {
  path: string;
  text: string;
  icon?: string;
  event?: string;
  event_params?: string;
}

const FOOTER_BACKGROUND = "#091112";
const FOOTER_BORDER = "rgba(255, 255, 255, 0.2)";
const FOOTER_MUTED_TEXT = "rgba(255, 255, 255, 0.6)";

const isExternalLink = (path: string) =>
  path.startsWith("http://") ||
  path.startsWith("https://") ||
  path.startsWith("mailto:");

export default function Footer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const menuLinks: footerItemsProps[] = [
    { path: getLink(LINK_TYPE.WALL), text: t("footer.home") },
    {
      path: getLink(LINK_TYPE.SECONDARY_MARKET),
      text: t("footer.reselling"),
      event: "click_reselling",
      event_params: "footer",
    },
  ];

  const socialLinks: footerItemsProps[] = [
    {
      path: getLink(LINK_TYPE.FACEBOOK),
      text: "Facebook",
      icon: facebook,
    },
    {
      path: getLink(LINK_TYPE.INSTAGRAM),
      text: "Instagram",
      icon: instagram,
    },
    {
      path: getLink(LINK_TYPE.LINKEDIN),
      text: "LinkedIn",
      icon: linkedin,
    },
  ];

  const supportLinks: footerItemsProps[] = [
    {
      path: "mailto:support@takyon.io",
      text: t("footer.contact"),
      event: "click_contact",
    },
  ];

  const legalLinks: footerItemsProps[] = [
    { path: getLink(LINK_TYPE.INNOVATIVE_STARTUP), text: t("footer.startup") },
    { path: getLink(LINK_TYPE.TOS), text: t("footer.tos") },
    { path: getLink(LINK_TYPE.PRIVACY), text: t("footer.privacy") },
    { path: getLink(LINK_TYPE.COOCKIES), text: t("footer.cookies") },
  ];

  const navigateToPage = (
    path: string,
    event?: string,
    eventParams?: string,
  ) => {
    if (event) {
      if (eventParams) {
        fireTagManagerEvent(event, { source: eventParams });
      } else {
        fireTagManagerEvent(event);
      }
    }
    navigate(path);
  };

  if (pathname !== "/" && !pagesWithFooter.some((l) => pathname.includes(l))) {
    return null;
  }

  return (
    <footer
      className="w-100"
      style={{
        background: FOOTER_BACKGROUND,
        borderTop: `1px solid ${FOOTER_BORDER}`,
      }}
    >
      <section
        className="container"
        style={{
          padding: "40px clamp(24px, 6.6vw, 100px)",
        }}
      >
        <div className="d-flex flex-column" style={{ gap: "40px" }}>
          <div
            className="d-flex flex-column flex-lg-row justify-content-between"
            style={{ gap: "60px" }}
          >
            <div
              className="d-flex flex-column"
              style={{ minWidth: "220px", flex: "1 1 0", gap: "20px" }}
            >
              <img
                src={takyonLight}
                alt="Takyon"
                style={{ width: "130px", height: "35.58px" }}
              />
              <div
                className="d-flex flex-column fs-body"
                style={{
                  color: FOOTER_MUTED_TEXT,
                  lineHeight: "22px",
                  letterSpacing: "-0.32px",
                }}
              >
                <span>Takyon S.r.l.</span>
                <span>Via Crema 15 - 20135,</span>
                <span>Milano</span>
                <span style={{ marginTop: "22px" }}>P.IVA 12424180961</span>
              </div>
            </div>

            <div
              className="d-flex flex-wrap align-items-start"
              style={{ columnGap: "60px", rowGap: "32px" }}
            >
              <div
                className="d-flex flex-column align-items-start"
                style={{ width: "168px", gap: "20px" }}
              >
                <span
                  className="fs-body"
                  style={{
                    color: FOOTER_MUTED_TEXT,
                    lineHeight: "20px",
                    letterSpacing: "-0.32px",
                  }}
                >
                  {t("footer.menu")}
                </span>

                <div
                  className="d-flex flex-column align-items-start"
                  style={{ gap: "20px" }}
                >
                  {menuLinks.map((menuItem) => (
                    <a
                      key={menuItem.path}
                      href={menuItem.path}
                      onClick={(event) => {
                        event.preventDefault();
                        navigateToPage(
                          menuItem.path,
                          menuItem.event,
                          menuItem.event_params,
                        );
                      }}
                      className="fs-body text-decoration-none"
                      style={{
                        color: "#FFFFFF",
                        lineHeight: "20px",
                        letterSpacing: "-0.32px",
                      }}
                    >
                      {menuItem.text}
                    </a>
                  ))}
                </div>
              </div>

              <div
                className="d-flex flex-column align-items-start"
                style={{ width: "120px", gap: "20px" }}
              >
                <span
                  className="fs-body"
                  style={{
                    color: FOOTER_MUTED_TEXT,
                    lineHeight: "20px",
                    letterSpacing: "-0.32px",
                  }}
                >
                  {t("footer.support")}
                </span>

                <div
                  className="d-flex flex-column align-items-start"
                  style={{ gap: "20px" }}
                >
                  {supportLinks.map((supportItem) => (
                    <a
                      key={supportItem.path}
                      href={supportItem.path}
                      onClick={() => {
                        if (supportItem.event) {
                          fireTagManagerEvent(supportItem.event);
                        }
                      }}
                      className="fs-body text-decoration-none"
                      style={{
                        color: "#FFFFFF",
                        lineHeight: "20px",
                        letterSpacing: "-0.32px",
                      }}
                    >
                      {supportItem.text}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div
            className="w-100"
            style={{ borderTop: `1px solid ${FOOTER_BORDER}` }}
          />

          <div
            className="d-flex flex-column flex-lg-row align-items-start"
            style={{ gap: "32px" }}
          >
            <div className="d-flex align-items-center" style={{ gap: "12px" }}>
              {socialLinks.map((socialItem) => (
                <a
                  key={socialItem.path}
                  href={socialItem.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    fireTagManagerEvent("click_social", {
                      channel: socialItem.path,
                    });
                  }}
                  aria-label={socialItem.text}
                >
                  <img
                    src={socialItem.icon}
                    alt={socialItem.text}
                    style={{ width: "20px", height: "20px" }}
                  />
                </a>
              ))}
            </div>

            <div
              className="d-flex flex-column flex-md-row flex-wrap align-items-start align-items-md-center justify-content-md-between w-100"
              style={{ gap: "12px 32px" }}
            >
              {legalLinks.map((legalItem) => (
                <a
                  key={legalItem.path}
                  href={legalItem.path}
                  target={isExternalLink(legalItem.path) ? "_blank" : undefined}
                  rel={
                    isExternalLink(legalItem.path)
                      ? "noopener noreferrer"
                      : undefined
                  }
                  onClick={(event) => {
                    if (isExternalLink(legalItem.path)) return;
                    event.preventDefault();
                    navigate(legalItem.path);
                  }}
                  className="text-decoration-none"
                  style={{
                    color: FOOTER_MUTED_TEXT,
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "-0.28px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {legalItem.text}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>
    </footer>
  );
}
